import { ScoreNumberingContext } from 'components/ReceptionScore/Numbering';
import { scoreCode } from 'components/ReceptionScore/model';
import { ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/css';
import { Tag, Tooltip, message } from 'antd';
import { Descendant, Text } from 'slate';
import { CustomSlateNodeType } from 'components/TemplateEditor/types';
import { flatBlockLevelDescendant } from 'components/TemplateEditor/utils/slateNodeHelper';
import DividingLine from 'components/TemplateEditor/renders/DividingLine';
import TagConcept from 'components/TemplateEditor/renders/TagConcept';
import EditorPreviewerFooter from './EditorPreviewerFooter';
import { DownloadOutlined, ShoppingOutlined } from '@ant-design/icons';
import { useTranslate } from '@tolgee/react';
import SafeImage from 'components/SafeImage';
import { FileIcon } from 'components/FileIcon';
import { openExternalURL } from 'utils/openExternalURL';
import EnhanceText from 'components/Dialogue/components/Message/EnhanceText';

const styles = {
  showAllContent: css`
    width: 100%;
    word-break: break-all;
  `,
  content: css`
    height: 48px;
    width: 100%;
    line-height: 24px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: pre-wrap;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  `,
  attachment: css`
    display: flex;
    align-items: center;
    margin-top: 6px;
    > * + * {
      margin-left: 6px;
    }
  `,
  tag: css`
    background-color: var(--fett-bg-app);
    border: 0;
    color: var(--fett-text-secondary);
  `,
  paramTextTag: css`
    color: var(--fett-scenario-purple-text);
  `,
  emoticon: css`
    display: inline;
  `,
  popContainer: css`
    color: #000;
    .ant-tooltip-inner {
      width: 320px;
      max-height: 240px;
      border-radius: 4px;
      box-shadow: 0px 12px 48px 16px rgba(0, 0, 0, 0.03), 0px 9px 28px 0px rgba(0, 0, 0, 0.05),
        0px 6px 16px -8px rgba(0, 0, 0, 0.08);
      color: rgba(0, 0, 0, 0.65);
      background-color: var(--fett-bg-panel);
      padding: 0;
      overflow-y: scroll;
      ::-webkit-scrollbar {
        width: 4px;
      }
      ::-webkit-scrollbar-track {
        border-radius: 10px;
        background-color: var(--fett-bg-panel);
      }
      ::-webkit-scrollbar-thumb {
        width: 4px;
        height: 36px;
        border-radius: 19px;
        background-color: rgba(210, 213, 217, 1);
      }
    }
    .ant-tooltip-arrow-content {
      background-color: rgba(255, 255, 255, 0.65);
    }
  `,
  popContent: css`
    padding: 16px;
  `,
  item: css`
    display: flex;
    align-items: center;
    color: var(--fett-text-tertiary);
    > span {
      flex: 0 0 auto;
      width: 40px;
      height: 40px;
      line-height: 32px;
      font-size: 18px;
      text-align: center;
      margin-right: 4px;
      background: var(--fett-erp-card-bg);
    }
    > section {
      width: 166px;
      > div {
        display: flex;
        align-items: center;
        height: 18px;
        &:first-of-type {
          margin-bottom: 4px;
          line-height: 18px;
          font-size: 12px;
          text-indent: 4px;
          background: var(--fett-erp-card-bg);
        }
        > span {
          flex: 0 0 auto;
          width: 50px;
          height: 100%;
          margin-right: 4px;
          background: var(--fett-erp-card-bg);
          &:first-of-type {
            width: 32px;
          }
        }
      }
    }
  `,
  logistics: css`
    width: 200px;
    background: var(--fett-bg-panel);
    border-radius: 4px;
    > header {
      margin-bottom: 4px;
      font-size: 12px;
      color: var(--fett-text-primary);
    }
    > div {
      display: flex;
      padding: 8px;
      border: solid 1px var(--fett-border-default);
      > span {
        flex: 0 0 auto;
        width: 40px;
        height: 40px;
        line-height: 32px;
        font-size: 18px;
        text-align: center;
        margin-right: 4px;
        background: var(--fett-erp-card-bg);
      }
      > section {
        flex: 1 0 0;
        > p {
          height: 16px;
          margin-bottom: 4px;
          background: var(--fett-erp-card-bg);
        }
        > div {
          display: flex;
          align-items: center;
          height: 16px;
          > span {
            display: inline-block;
            width: 50px;
            height: 16px;
            margin-right: 4px;
            background: var(--fett-erp-card-bg);
          }
        }
      }
    }
    > ul {
      margin: 4px 0;
      padding: 0;
      list-style: none;
      > li {
        display: flex;
        align-items: center;
        height: 24px;
        font-size: 12px;
        color: var(--fett-text-tertiary);
        > div {
          flex: 1 0 0;
          height: 12px;
          margin-left: 4px;
          background: var(--fett-erp-card-bg);
        }
      }
    }
    > footer {
      display: flex;
      justify-content: flex-end;
      > div {
        display: inline-block;
        padding: 0 8px;
        line-height: 24px;
        font-size: 12px;
        color: var(--fett-text-tertiary);
        background: #e1e4eb;
        border-radius: 4px;
      }
    }
  `,
};

interface EditorPreviewerProps {
  value: Descendant[];
  placeholder?: ReactNode;
  hoverPop?: boolean;
  useFooterPlugin?: boolean;
  popUseFooterPlugin?: boolean;
  getStickerUrl?: (str: string | undefined) => Promise<string | undefined>;
}

const EditorPreviewer = ({
  value,
  placeholder = null,
  hoverPop = false,
  useFooterPlugin = true,
  popUseFooterPlugin = false,
  getStickerUrl,
}: EditorPreviewerProps) => {
  const { t } = useTranslate();
  const [fileList, setFileList] = useState<any[]>([]);

  const scoreNumbers = useContext(ScoreNumberingContext);
  const { result: content, attachment } = useMemo(() => {
    return value
      ? flatBlockLevelDescendant(value).reduce(
          (res, node, index) => {
            const { result, attachment } = res;
            const key = `${node.type}_${index}`;
            if (node.type === CustomSlateNodeType.ITEM) {
              result.push(<ItemPreview title={t('recommend_item')} />);
              return res;
            }
            if (node.type === CustomSlateNodeType.LOGISTICS) {
              result.push(<LogisticsPreview />);
            }
            if (Text.isText(node)) {
              result.push(<EnhanceText key={key} text={node.text || '-'} />);
              return res;
            }
            switch (node.type) {
              case CustomSlateNodeType.DIVIDING_LINE:
                result.push(<DividingLine key={key} />);
                break;
              case CustomSlateNodeType.CONCEPT:
                if (scoreCode(node.label) && scoreNumbers[scoreCode(node.label)!]) {
                  result.push(<span key={key}>{scoreNumbers[scoreCode(node.label)!]}</span>);
                  break;
                }
                if (node.label === '好评') {
                  result.push(<span>{` 1 `}</span>);
                  break;
                }

                if (node.label === '差评') {
                  result.push(<span>{` 2 `}</span>);
                  break;
                }

                result.push(
                  <TagConcept key={key} element={node} attributes={{}}>
                    {null}
                  </TagConcept>,
                );
                break;
              case CustomSlateNodeType.PARAMTEXT:
                result.push(
                  <Tag key={key} color="#E6E4F2" className={styles.paramTextTag}>
                    {node.label}
                  </Tag>,
                );
                break;
              case CustomSlateNodeType.PARAMHOLDER:
                result.push(
                  <Tag key={key} color="#E6E4F2" className={styles.paramTextTag}>
                    {node.label}
                  </Tag>,
                );
                break;
              case CustomSlateNodeType.FILE:
                const { originalFilename, url } = node.data;
                const dotIndex = originalFilename.lastIndexOf('.');
                const name = originalFilename.substring(0, dotIndex);
                const ext = originalFilename.substring(dotIndex, originalFilename.length);

                if (['.jpg', '.jpeg', '.png'].includes(ext)) {
                  attachment.push(<SafeImage preview src={url} width={56} />);
                } else {
                  attachment.push(
                    <div
                      className="group relative w-14 h-14 flex flex-col justify-center items-center bg-[var(--fett-bg-info-tip)] gap-0.5"
                      title={originalFilename}
                    >
                      <FileIcon className="text-[22px]" ext={ext} />
                      <span className="w-10 text-xs text-content-200 text-center overflow-hidden text-ellipsis whitespace-nowrap">
                        {name}
                      </span>
                      <div
                        className="hidden group-hover:flex items-center justify-center absolute top-0 right-0 bottom-0 left-0 bg-[#000000] bg-opacity-50 cursor-pointer"
                        onClick={async () => {
                          try {
                            openExternalURL(url, '__blank');
                          } catch (err) {
                            message.error(t('translation.操作失败，请稍后再试'));
                          }
                        }}
                      >
                        <DownloadOutlined className="!text-fill-0" />
                      </div>
                    </div>,
                  );
                }
                break;
              default:
                break;
            }
            return res;
          },
          { result: [] as ReactNode[], attachment: [] as ReactNode[] },
        )
      : { result: [placeholder], attachment: [] };
  }, [placeholder, value, t, scoreNumbers]);

  useEffect(() => {
    const imageEl = value.filter((v) => v.type === CustomSlateNodeType.IMAGE);
    const allImages: Array<{ uid: string; url?: string }> = [];
    imageEl.forEach((image) => {
      image.images.forEach(async ({ imageId }, index) => {
        allImages.push({
          uid: `${imageId}#${index}`,
          url: imageId,
        });
      });
    });
    Promise.all(
      allImages.map((image) => {
        return new Promise((resolve) => {
          if (image.url && getStickerUrl && image.url?.includes('@')) {
            getStickerUrl(image.url)
              .then((url) => {
                resolve({
                  ...image,
                  url,
                });
              })
              .catch(() => {
                resolve(image);
              });
          } else {
            resolve(image);
          }
        });
      }),
    ).then(setFileList);
  }, [value, getStickerUrl]);

  const contentNode = useMemo(() => {
    if (hoverPop) {
      return (
        <Tooltip
          placement="top"
          mouseEnterDelay={0.5}
          title={
            <div className={styles.popContent}>
              {content}
              {attachment.length > 0 && <div className={styles.attachment}>{attachment}</div>}
              {popUseFooterPlugin &&
                fileList.map((v) => <EditorPreviewerFooter fileList={[v]} />)}
            </div>
          }
          overlayClassName={styles.popContainer}
        >
          <div className={styles.content}>{content}</div>
        </Tooltip>
      );
    }
    return (
      <div className={styles.content}>
        {content}
        {attachment.length > 0 && <div className={styles.attachment}>{attachment}</div>}
      </div>
    );
  }, [hoverPop, content, popUseFooterPlugin, attachment, fileList]);

  return (
    <div>
      {content.length > 0 && contentNode}
      {useFooterPlugin && fileList.map((v) => <EditorPreviewerFooter fileList={[v]} />)}
    </div>
  );
};

export function ItemPreview({ title }: { title: ReactNode }) {
  return (
    <div className={styles.item}>
      <span>
        <ShoppingOutlined />
      </span>
      <section>
        <div>{title}</div>
        <div>
          <span />
          <span />
        </div>
      </section>
    </div>
  );
}

export function LogisticsPreview() {
  const { t } = useTranslate();
  return (
    <div className={styles.logistics}>
      <header>{t('trade.logisticsInfo')}</header>
      <div>
        <span>
          <ShoppingOutlined />
        </span>
        <section>
          <p />
          <div>
            <span />
            <span />
          </div>
        </section>
      </div>
      <ul>
        <li>
          {t('物流公司')}
          <div />
        </li>
        <li>
          {t('预计送达时间')}
          <div />
        </li>
        <li>
          {t('trade.latestLogisticsTime')}
          <div />
        </li>
      </ul>
      <footer>
        <div>{t('feedback.查看详情')}</div>
      </footer>
    </div>
  );
}

export default EditorPreviewer;
